import { AlertTriangle, Inbox, Loader2, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export function LoadingState({ label = "Loading…", testid = "loading-state" }) {
  return (
    <div data-testid={testid} className="flex items-center gap-3 p-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin text-primary" /> {label}
    </div>
  );
}

export function EmptyState({ title, description, action, icon: Icon = Inbox, testid = "empty-state" }) {
  return (
    <div data-testid={testid} className="flex flex-col items-start gap-3 p-8 md:p-10">
      <div className="rounded-lg border border-dashed p-3 text-muted-foreground"><Icon className="h-5 w-5" /></div>
      <div>
        <p className="font-display text-lg font-bold">{title}</p>
        {description && <p className="mt-1 max-w-lg text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry, testid = "error-state" }) {
  return (
    <div data-testid={testid} role="alert" className="m-4 flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-destructive"><AlertTriangle className="h-4 w-4" /> Something went wrong</div>
      <p className="text-sm text-foreground/80" data-testid={`${testid}-message`}>{message}</p>
      {onRetry && <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testid}-retry`}><RotateCw className="mr-2 h-3.5 w-3.5" />Retry</Button>}
    </div>
  );
}

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-4 md:mb-8 md:flex-row md:items-end md:justify-between fade-up">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="page-title">{title}</h1>
        {description && <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

const TONES = {
  teal: "bg-teal-500/10 text-teal-600 dark:text-teal-300 border-teal-500/30",
  cyan: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
  amber: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  rose: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  slate: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30",
  emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  indigo: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30",
};
const VALUE_TONES = {
  confirmed_procurement: "emerald", active_project_signal: "teal", strategic_prospect: "cyan", partner_prospect: "cyan", competitor: "rose", not_assessed: "slate", further_research_required: "amber",
  rejected: "rose", won: "emerald", lost: "rose", qualified: "teal", proposal: "cyan", negotiation: "cyan", engaged: "teal",
  high: "rose", medium: "amber", low: "slate", done: "emerald", in_progress: "cyan", open: "teal", cancelled: "slate",
  customer: "emerald", competitor: "rose", opted_out: "rose", opted_in: "emerald", active: "teal", connected: "emerald", not_connected: "slate",
  verified: "emerald", corroborated: "teal", inferred: "cyan", unknown: "slate", contradicted: "rose", unverified: "amber",
  succeeded: "emerald", failed: "rose", timed_out: "rose", queued: "slate", running: "cyan", draft: "slate", paused: "amber", archived: "slate",
};

export function Pill({ children, tone, value, testid }) {
  const cls = TONES[tone || VALUE_TONES[value] || "slate"];
  return <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>{children}</span>;
}

export function DemoBadge() {
  return <span data-testid="demo-badge" className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 font-mono text-[10px] font-bold tracking-widest text-amber-600 dark:text-amber-300">DEMO · SYNTHETIC DATA</span>;
}

export function ScoreBar({ label, value, testid }) {
  const v = value ?? null;
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-mono">{v == null ? "Not assessed" : v}</span></div>
      <div className="h-1.5 rounded-full bg-muted"><div className="h-1.5 rounded-full bg-primary transition-[width] duration-500" style={{ width: `${v || 0}%` }} /></div>
    </div>
  );
}
